import React, {useEffect, useMemo, useState} from "react";
import {removeIntroductions} from "../../../../api/lifecycle/introduction/IntroductionService";
import SearchSection from "../../../../components/search/SearchSection";
import Selector from "../../../../components/selector/Selector";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {INTRODUCTION_LIST_HEADER} from "../../../../components/table/header/Headers";
import {searchListener, searchWithChildren} from "../../../../components/commonmethod/simmsDrawerMethod";
import Search from "../../../../components/search/Search";
import {CodeSpecNum, ContentCategory} from "../../../../constant/CodeSpecNum";
import {SearchProgressComboBox} from "../../../../components/select/SearchProgressComboBox";
import {getIndividualSetting, resetIndividualSetting, setIndividualSetting} from "../../../../api/user/UserAccountService";
import {extractObject, initHeaderOrders, nonObject, simmsColumnsToJsGridHeaders} from "../../../../components/commonmethod/CommonMethod";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../components/drawer/simms/SimmsDrawerSearch";
import {ToolbarDataTransfer} from "@bavuchoko/js-grid";
import SimmsJsGrid from "../../../../components/js-grid/SimmsJsGrid";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../../components/pageTabs/pageTabsStorage";
import {useNavigate} from "react-router-dom";
import {useExcelDownloadClick} from "../../../../components/commonmethod/excelMethod";
import JsGridExcelGridOpenButton from "../../../../components/js-grid/JsGridExcelGridOpenButton";

function IntroductionListType({checked, data, date, dataHook, pageInfo}) {
    const navigate = useNavigate();
    const {handleSearch, handlePageSizeChange, saveConditions, refresh, handleGridPageChange} = dataHook;
    const handleDownloadClick = useExcelDownloadClick(saveConditions);

    const [header, setHeader] = useState({order: INTRODUCTION_LIST_HEADER, exclude: []});
    const searchDate = {startDate: saveConditions.startDate ?? date.startDate, endDate: saveConditions.endDate ?? date.endDate};

    useEffect(() => {
        getIndividualSetting().then((r) => {
            initHeaderOrders(r, INTRODUCTION_LIST_HEADER, setHeader, [], () => {}, () => {}, "introduction");
        });
    }, []);

    const handleRowClick = (row) => {
        const id = row?.id;
        if (id == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, "자산도입 상세"),
            url: `/lifecycle/introduction/${id}`,
        });
    };

    const deleteIntroduction = async (rows) => {
        if (!rows?.length) {
            handleActiveToast({message: "삭제할 도입을 선택해주세요."});
            return;
        }
        if (!window.confirm("삭제하시겠습니까?")) {
            return;
        }
        const ids = rows.map((row) => row.id).filter((id) => id != null);
        await removeIntroductions({ids}).then((response) => {
            if (response.status === 200) {
                handleActiveToast({message: "데이터가 삭제되었습니다"});
            } else if (response.status === 201) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else if (response.status === 403) {
                handleActiveError({message: "권한이 없습니다."});
            } else {
                handleActiveError({message: "알 수 없는 에러 발생."});
            }
        });
        checked?.setChecked?.([]);
        refresh();
    };

    const introductionJsGridHeader = useMemo(() => simmsColumnsToJsGridHeaders(header), [header]);
    const excelGridSearch = useMemo(() => extractObject(saveConditions), [saveConditions]);

    return (
        <div className="container-height">
            <Search
                refresh={refresh}
                entity="introduction"
                searchConditions={saveConditions}
                handleSearch={handleSearch}
                handlePageSizeChange={handlePageSizeChange}
                pageInfo={pageInfo}
                size
                detail
                date={searchDate}
                searchBar={{placeHolder: "요청제목, 요청코드"}}
            >
                <SearchSection>
                    <Selector
                        showLabel
                        onChange={(value) => searchWithChildren(handleSearch, "introductionCode", extractObject(saveConditions), value)}
                        data={saveConditions.introductionCode}
                        parentKey={CodeSpecNum.IntroductionRequestCode}
                        recursive
                        entity="code"
                        title="코드명"
                    />
                    <Selector
                        showLabel
                        auth
                        onChange={(value) => searchListener(handleSearch, "operator", extractObject(saveConditions), value)}
                        data={saveConditions.operator}
                        uniqueKey="operator"
                        entity="userAccount"
                        title="운영담당자"
                    >
                        <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                        <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                        <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                    </Selector>
                    <Selector
                        showLabel
                        auth
                        onChange={(value) => searchListener(handleSearch, "requester", extractObject(saveConditions), value)}
                        data={saveConditions.requester}
                        uniqueKey="requester"
                        entity="userAccount"
                        title="요청자"
                    >
                        <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                        <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                        <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                    </Selector>
                    <Selector
                        showLabel
                        auth
                        onChange={(value) => searchListener(handleSearch, "user", extractObject(saveConditions), value)}
                        data={saveConditions.user}
                        uniqueKey="user"
                        entity="userAccount"
                        title="검수자"
                    >
                        <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                        <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                        <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                    </Selector>
                    <SearchProgressComboBox
                        target="progressNumericValue"
                        tableName={ContentCategory.INTRODUCTION}
                        value={saveConditions}
                        onChange={(v) => nonObject(saveConditions, v, handleSearch, "progressNumericValue")}
                    />
                </SearchSection>
            </Search>

            <div className="container-height-body">
                <SimmsJsGrid
                    loading={dataHook.loading}
                    resizable={true}
                    data={data}
                    theme="linear"
                    style={{minHeight: 0, height: "100%"}}
                    header={introductionJsGridHeader}
                    onHeaderSave={(v) => setIndividualSetting({headers: {introduction: v}})}
                    onHeaderReset={() => resetIndividualSetting("headers", "introduction")}
                    enableRowSelection={true}
                    onRowClick={handleRowClick}
                    onPageChange={handleGridPageChange}
                    toolbarEnd={() => (
                        <div className="flex items-center gap-2">
                            <JsGridExcelGridOpenButton entityName="ExcelIntroductionDto" search={excelGridSearch} />
                            <ToolbarDataTransfer
                                hint="선택 항목 삭제"
                                busyHint="삭제 중…"
                                overlayLabel="삭제 중…"
                                onTransfer={deleteIntroduction}
                            >
                                {(ctx) => (
                                    <button
                                        type="button"
                                        className="text-[13px] px-3 py-0.5 text-black rounded-4 border disabled:cursor-not-allowed"
                                        onClick={() => {
                                            if (ctx.selectedCount === 0) {
                                                handleActiveToast({message: "삭제할 도입을 선택해주세요."});
                                                return;
                                            }
                                            void ctx.transferSelected();
                                        }}
                                    >
                                        삭제
                                    </button>
                                )}
                            </ToolbarDataTransfer>
                            <button
                                type="button"
                                className="text-[13px] px-3 py-0.5 border text-white bg-themeDark rounded-4"
                                onClick={() => navigate("/lifecycle/introduction/create")}
                            >
                                등록
                            </button>
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

export default IntroductionListType;
